import { useQuery } from "@apollo/client";
import { GET_SESSIONS_BY_INVITE_AND_USER } from "../gql";

interface SessionData {
  id: string;
  status: string;
  createdAt: string;
  completedAt?: string | null;
  conversation?: any[] | null;
}

interface SessionsResponse {
  conversations: {
    edges: Array<{
      node: SessionData;
    }>;
  };
}

interface SessionsVariables {
  connectionCode: string;
  participantUserId: string;
}

export function useSessionsByInviteAndUser(
  connectionCode: string,
  participantUserId: string,
  options: { skip?: boolean } = {},
) {
  const { data, loading, error } = useQuery<
    SessionsResponse,
    SessionsVariables
  >(GET_SESSIONS_BY_INVITE_AND_USER, {
    variables: {
      connectionCode,
      participantUserId,
    },
    skip: !connectionCode || !participantUserId || options.skip,
    errorPolicy: "all",
  });

  const sessions = data?.conversations?.edges?.map((edge) => edge.node) || [];

  // Helper function to check if a session is resumable
  const isSessionResumable = (session: SessionData): boolean => {
    // Handle null/undefined conversation
    if (!session.conversation) {
      return false;
    }

    // Parse conversation if it's a string (JSONB from database)
    let messages: any[] = [];
    try {
      if (typeof session.conversation === "string") {
        messages = JSON.parse(session.conversation);
      } else if (Array.isArray(session.conversation)) {
        messages = session.conversation;
      } else {
        return false;
      }
    } catch (e) {
      console.error("Failed to parse conversation:", e);
      return false;
    }

    // Check if messages array is empty
    if (!Array.isArray(messages) || messages.length === 0) {
      return false;
    }

    // Count agent messages (questions)
    const agentMessages = messages.filter(
      (msg: any) => msg.role === "assistant",
    );

    // Count user messages
    const userMessages = messages.filter((msg: any) => msg.role === "user");

    // Need at least one question and meaningful interaction
    if (agentMessages.length === 0) {
      return false;
    }

    // If only greeting exchanged, not worth resuming
    if (agentMessages.length === 1 && userMessages.length <= 1) {
      return false;
    }

    return true;
  };

  // Find the latest session
  const latestSession = sessions.length > 0 ? sessions[0] : null;

  // Find the latest incomplete session that's actually resumable
  const latestIncompleteSession = sessions.find(
    (session) => session.status !== "completed" && isSessionResumable(session),
  );

  console.log("Sessions found:", {
    total: sessions.length,
    latestSession: latestSession?.id,
    latestIncompleteSession: latestIncompleteSession?.id,
    statuses: sessions.map((s) => s.status),
    resumableSessions: sessions.filter(
      (s) => s.status !== "completed" && isSessionResumable(s),
    ).length,
  });

  return {
    sessions,
    latestSession,
    latestIncompleteSession,
    loading,
    error,
  };
}
